<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <h1 class="title">Payroll {{ payroll.legal_entity.name }} ({{ dateFormat(payroll.month) }})</h1>
        <app-button ref="recalculate" @click.native="onRecalculatePayroll" theme="warn">Recalculate</app-button>
      </app-cells>
      <div v-if="needsRecalculation" class="payroll-alert" role="alert">
        Data used in this payroll changed after it was created. Recalculate before completion.
        <ul v-if="pendingEvents.length" class="payroll-alert__events">
          <li v-for="event in pendingEvents" :key="event.id" class="payroll-alert__event">
            <span>{{ eventTitle(event) }}</span>
            <span class="payroll-alert__meta">
              <template v-if="eventAuthor(event)">{{ eventAuthor(event) }}, </template>{{ dateTimeFormat(event.created_at) }}
            </span>
          </li>
        </ul>
      </div>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Employees:</span>
              <span>{{ payroll.employees_pax }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Unpaid leave:</span>
              <span>{{ amountLabel(payroll.unpaid_leave_deduction) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Gross:</span>
              <span>{{ payroll.gross.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Accrued salary:</span>
              <span>{{ payroll.accrued_salary.toLocaleString() }} TSh</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Bank:</span>
              <span>{{ payroll.bank_amount.toLocaleString() }} TSh</span>
            </div>
            <div class="info-cards__row-item">
              <span>Cash:</span>
              <span>{{ payroll.cash_amount.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>PAYE:</span>
              <span>{{ payroll.paye_amount.toLocaleString() }} TSh</span>
            </div>
            <div class="info-cards__row-item">
              <span>NO TIN:</span>
              <span>{{ payroll.paye_no_tin.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>SDL:</span>
              <span>{{ payroll.sdl_amount.toLocaleString() }} TSh</span>
            </div>
            <div class="info-cards__row-item">
              <span>WCF:</span>
              <span>{{ payroll.wcf_amount.toLocaleString() }} TSh</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>WHT:</span>
              <span>{{ payroll.wht_amount.toLocaleString() }} TSh</span>
            </div>
            <div class="info-cards__row-item">
              <span>Advances:</span>
              <span>{{ payroll.advance_amount.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>NHIF:</span>
              <span>{{ payroll.nhif_amount.toLocaleString() }} TSh</span>
            </div>
            <div class="info-cards__row-item">
              <span>HESLB:</span>
              <span>{{ payroll.heslb_amount.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>NSSF:</span>
              <span>{{ payroll.nssf_amount.toLocaleString() }} TSh</span>
            </div>
            <div class="info-cards__row-item">
              <span>Staff:</span>
              <span>{{ payroll.staff_loan_amount.toLocaleString() }} TSh</span>
            </div>
          </div>
        </div>
      </div>
      <div class="downloads downloads--mb">
        <div class="downloads__item" :class="{'downloads__item--loading': control_loader}">
          <div class="downloads__left">
            <div class="downloads__info">
              <span class="downloads__name downloads__name--no-events">Completed</span>
            </div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Status:</span>
              <app-switcher
                :readonly="info_completed || needsRecalculation || control_loader"
                v-model="info_completed"
                @change="onUpdateControlInfo({completed: info_completed})"
              />
            </div>
            <template v-if="info_completed && !control_loader">
              <div class="downloads__values">
                <span class="downloads__key">Completed at:</span>
                <span class="downloads__value downloads__width">{{ dateTimeFormat(payroll.control_info.completed_at) }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Completed by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(payroll.control_info.completed_by?.crop.crop_url)" alt="Person">
                  </div>
                  <span class="downloads__value">{{ payroll.control_info.completed_by?.full_name || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <app-tabs :tabs="tabs" />
      <transition name="fade" mode="out-in">
        <router-view :payroll="payroll" />
      </transition>
    </template>
  </div>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import {
  getEmployeesPayroll,
  postEmployeesPayrollRecalculate,
  patchEmployeesPayrollControlInfo
} from '@/http'
import { format, parse, parseISO } from 'date-fns'
import {
  eventAuthor,
  eventTitle,
  pendingEvents
} from '@/views/employees/payroll/payrollRecalculationEvents'

export default {
  name: 'EmployeePayrollLayout',
  components: { AppTabs },
  data() {
    return {
      is_loaded: false,
      tabs: [
        { name: 'employees-payroll-employees', label: 'Employees' },
        { name: 'employees-payroll-downloads', label: 'Downloads' },
        { name: 'employees-payroll-taxes', label: 'Taxes' },
      ],
      payroll: {},
      control_loader: false,
      info_completed: false,
      requestId: 0,
    }
  },
  computed: {
    needsRecalculation() {
      return Boolean(this.payroll.control_info && this.payroll.control_info.needs_recalculation === true)
    },
    pendingEvents() {
      return pendingEvents(this.payroll)
    },
  },
  watch: {
    '$route.params.id'() {
      this.is_loaded = false
      this.fetchPayrollData()
    },
  },
  created() {
    this.fetchPayrollData()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  methods: {
    eventTitle,
    eventAuthor,
    fetchPayrollData() {
      const requestId = ++this.requestId
      return getEmployeesPayroll(this.$route.params.id).then(res => {
        if (requestId !== this.requestId) return
        this.payroll = res.data
        document.title = `Payroll ${this.payroll.legal_entity.name} (${this.dateFormat(this.payroll.month)}) | Altezza Board`
        this.info_completed = !!this.payroll.control_info.completed
        this.is_loaded = true
      })
    },
    amountLabel(value) {
      if (value === null || value === undefined || value === '') return '-'
      const amount = Number(value)
      return `${Number.isFinite(amount) ? amount.toLocaleString() : value} TSh`
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy')
    },
    onRecalculatePayroll() {
      this.$refs.recalculate.preload = true
      postEmployeesPayrollRecalculate(this.$route.params.id)
        .then(() => this.fetchPayrollData())
        .catch(() => {
          this.$notify({
            type: 'error',
            title: 'Payroll could not be recalculated or refreshed',
          })
        })
        .finally(() => {
          if (this.$refs.recalculate) this.$refs.recalculate.preload = false
        })
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onUpdateControlInfo() {
      if (this.needsRecalculation || this.control_loader) {
        this.info_completed = Boolean(this.payroll.control_info && this.payroll.control_info.completed)
        return
      }
      this.control_loader = true
      patchEmployeesPayrollControlInfo(this.$route.params.id, { completed: true })
        .then(() => {
          this.fetchPayrollData()
          this.$notify({
            type: 'success',
            title: 'Change saved',
          })
          this.control_loader = false
        })
        .catch(() => {
          this.info_completed = Boolean(this.payroll.control_info && this.payroll.control_info.completed)
          this.$notify({
            type: 'error',
            title: 'Payroll status could not be updated',
          })
          this.control_loader = false
        })
    },
  }
}
</script>

<style lang="sass" scoped>
.payroll-alert
  margin: 0 0 20px
  padding: 12px 16px
  border-radius: 6px
  color: #9a3412
  background-color: #ffedd5

  &__events
    margin: 8px 0 0
    padding: 0 0 0 20px
    list-style: disc

  &__event
    padding: 2px 0

  &__meta
    margin-left: 8px
    opacity: 0.8
</style>
